import React from 'react';
import styled from 'styled-components';

import { ClassifierDto, LiveStreamingDto } from '@/application/interfaces/pegasus.gateway';

import CoursesList from '../course-list/CourseList';
import CourseSlider from '../course-slider';
import LiveItem from '../live-item/live-item';

type Props = {
  classifier: ClassifierDto;
  level?: number;
  liveStreaming?: LiveStreamingDto;
};

const Container = styled.div`
  padding: 0rem 0.5rem;
`;

const TitleLevelZero = styled.h1`
  font-family: Aprova Nova;
  padding-top: 1rem;
  padding-bottom: 1rem;
  font-size: 56px;
  font-weight: 700;
  line-height: 48px;
  text-align: left;
`;

const ContainerLevelOne = styled.header`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Divider = styled.hr`
  width: 100%;
  border-top: 1px solid;
  border-color: var(--neutral-color-600);
  padding-top: 0.5rem;
  padding-bottom: 1rem;
  margin-top: 0.5rem;
`;

const TitleLevelOne = styled.h1`
  font-family: Aprova Nova;
  padding-top: 1rem;
  padding-bottom: 0.5rem;
  font-size: 32px;
  font-weight: 700;
  line-height: 38px;
  letter-spacing: -1px;
  text-align: left;
`;
const MoreInfoLevelOne = styled.p`
  font-family: Aprova Nova;
  color: #caccce;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  text-align: left;
`;

const ContainerLevelTwo = styled.div`
  margin-bottom: 3rem;
`;

const TitleLevelTwo = styled.h1`
  font-family: Aprova Nova;
  padding-top: 1rem;
  padding-bottom: 0.5rem;
  font-size: 24px;
  font-weight: 400;
  line-height: 29px;
  letter-spacing: 0px;
  text-align: left;
`;

const hasChildClassifiers = (classifier: ClassifierDto) => classifier?.childClassifiers.length > 0;

const ShowChildClassifierNameAndAmount: React.FC<Props> = ({ classifier }) => {
  let name, amount;

  if (classifier.childClassifiers.length === 0) {
    amount = classifier.courses.length;
    name = amount > 1 ? 'cursos' : 'curso';
  } else {
    amount = classifier.childClassifiers.length;
    name =
      amount > 1
        ? `${classifier.childClassifiers[0].classificationLevel.name}s`
        : classifier.childClassifiers[0].classificationLevel.name;
  }

  return (
    <MoreInfoLevelOne>
      {amount} {name.toLocaleLowerCase()}
    </MoreInfoLevelOne>
  );
};

const ClassifierLevelTwo: React.FC<Props> = ({ classifier }) => {
  return (
    <ContainerLevelTwo>
      <TitleLevelTwo>{classifier.name}</TitleLevelTwo>
      <CourseSlider courses={classifier.courses} />
    </ContainerLevelTwo>
  );
};

const ClassifierLevelOne: React.FC<Props> = ({ classifier }) => {
  return (
    <>
      <ContainerLevelOne>
        <TitleLevelOne>{classifier.name}</TitleLevelOne>
        <ShowChildClassifierNameAndAmount classifier={classifier} />
      </ContainerLevelOne>
      <Divider />
      {hasChildClassifiers(classifier) ? (
        <>
          {classifier?.childClassifiers.map((currentClassifier) => (
            <ClassifierLevelTwo key={currentClassifier.guid} classifier={currentClassifier} />
          ))}
        </>
      ) : (
        <CourseSlider courses={classifier.courses} />
      )}
    </>
  );
};

const ClassifierLevelZero: React.FC<Props> = ({ classifier, liveStreaming }) => {
  return (
    <Container>
      <TitleLevelZero>{classifier.name}</TitleLevelZero>
      {liveStreaming && <LiveItem status={'Rolando Agora'} liveStreaming={liveStreaming} />}
      {hasChildClassifiers(classifier) ? (
        classifier.childClassifiers.map((currentClassifier) => (
          <ClassifierLevelOne key={currentClassifier.guid} classifier={currentClassifier} />
        ))
      ) : (
        <CoursesList courses={classifier.courses} />
      )}
    </Container>
  );
};

const ClassifierItem: React.FC<Props> = ({ classifier, liveStreaming }) => {
  return <ClassifierLevelZero classifier={classifier} liveStreaming={liveStreaming} />;
};

export default ClassifierItem;
